import { Autocomplete, Stack, type SxProps, TextField, type Theme } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { useState } from "react";

import { mergeSxProps } from "../lib/mui/mergeSxProps";
import { useDeprecatedSearchUsers, type User } from "./api/useDeprecatedSearchUsers";

interface UserSearchProps {
  type: "AGENT" | "FACILITY";
  value: User | User[] | null;
  placeholder: string;
  onBlur?: () => void;
  onSelect?: (value: User | null) => void;
  onMultiSelect?: (value: User[]) => void;
  maxHeight?: number;
  width?: number;
  multiple?: boolean;
  sx?: SxProps<Theme>;
}

export function UserSearch(props: UserSearchProps) {
  const {
    type,
    value,
    placeholder,
    width,
    maxHeight,
    onBlur,
    onSelect,
    onMultiSelect,
    multiple = false,
    sx,
  } = props;
  const [searchInput, setSearchInput] = useState("");

  const { data: { list: options } = { list: [] }, isFetching } = useDeprecatedSearchUsers({
    type,
    searchInput,
  });

  const handleChange = (value: User | User[] | null) => {
    if (Array.isArray(value)) {
      onMultiSelect?.(value);
    } else {
      onSelect?.(value);
    }
  };

  const mergedOptions = value ? [options, value].flat() : options;

  return (
    <Autocomplete
      filterSelectedOptions
      size="small"
      value={value}
      multiple={multiple}
      limitTags={1}
      loading={isFetching}
      loadingText="Search..."
      options={mergedOptions}
      getOptionLabel={(user) => user.name}
      getOptionKey={(option) => option.userId}
      renderOption={(renderProps, option) => {
        const { userId, name } = option;
        return (
          <li {...renderProps}>
            <Stack alignItems="space-between" direction="column">
              <span>{name}</span>
              <Text variant="body2">{userId}</Text>
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <div
                style={{
                  maxHeight,
                  overflowY: "auto",
                }}
              >
                {params.InputProps.startAdornment}
              </div>
            ),
          }}
        />
      )}
      sx={mergeSxProps({ width }, sx)}
      disableCloseOnSelect={multiple}
      inputMode="search"
      noOptionsText={
        <Text variant="body2">
          {searchInput ? "No results found" : "Start typing to search..."}
        </Text>
      }
      isOptionEqualToValue={(option, value) => option.name === value.name}
      // We want to filter at the backend. hence disabling auto filtering in Autocomplete. This allows us to filter by Ids, emails etc..
      filterOptions={(options) => options}
      onInputChange={(_, value) => {
        setSearchInput(value ?? searchInput);
      }}
      onChange={(_, value) => {
        handleChange(value);
      }}
      onBlur={onBlur}
    />
  );
}
